/**
 * Mantenimiento, en el riel del paso 3.
 *
 * Estado (encendido, desde cuándo, ritmo, autonomía), el resumen que justifica
 * el retainer, cuándo corre la revisión semanal, y Search Console como
 * acordeón que se abre AHÍ MISMO. El reporte al cliente y Publicar viven en
 * la fila de pestañas del proyecto (Eduardo, 2026-09-06: "el botón Publish ya
 * está arriba"). Con el mantenimiento apagado, solo la opción de encenderlo.
 *
 * El salto que importa es semiautomático → automático: es el único nivel que
 * manda algo afuera sin que nadie lo lea. Por eso pasar a automático pregunta y
 * los otros dos no — el criterio de la REGLA #24.
 */
import { useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../../ui/DetailSection.js';
import { Button } from '../../ui/Button.js';
import { Dropdown } from '../../ui/Dropdown.js';
import { Pill } from '../../PillGroup.js';
import { LoadingState } from '../../ui/LoadingState.js';
import { useConfirm } from '../../ui/ConfirmDialog.js';
import { useToast } from '../../ui/Toast.js';
import type { ProjectRow } from '../../../hooks/useProjects.js';
import type { Autonomia } from '../../../hooks/useMantenimiento.js';
import {
  useMantenimiento, useEncenderMantenimiento, useFijarAutonomia,
  useSearchConsole, useAjustarRevision,
} from '../../../hooks/useMantenimiento.js';
import { CuerpoSearchConsole } from './SeccionSearchConsole.js';

const menor = {
  fontSize: 'var(--evo-fs-tiny)',
  color: 'var(--evo-fg-4)',
  fontFamily: 'var(--evo-font)',
} as const;

const etiqueta = {
  ...menor,
  textTransform: 'uppercase',
  letterSpacing: 'var(--evo-ls-caps)',
} as const;

/** Los siete días, con el nombre en el idioma de la interfaz. 0 = domingo. */
function nombresDeDias(lang: string): Array<{ value: string; label: string }> {
  // El 6/9/2026 es domingo: base fija para que el orden no dependa de hoy.
  return Array.from({ length: 7 }, (_, d) => ({
    value: String(d),
    label: new Date(Date.UTC(2026, 8, 6 + d, 12)).toLocaleDateString(lang, { weekday: 'long' }),
  }));
}

type Fila = 'search-console';

function leerAbierta(clave: string): boolean {
  try { return localStorage.getItem(`evo-riel:${clave}`) === '1'; } catch { return false; }
}

/**
 * Una fila del acordeón: título, el dato corto a la derecha, y el cuerpo
 * debajo cuando está abierta. El navegador recuerda cuáles dejaste abiertas.
 */
function Acordeon({ clave, titulo, meta, children }: { clave: string; titulo: string; meta: React.ReactNode; children: React.ReactNode }) {
  const [abierta, setAbierta] = useState(() => leerAbierta(clave));
  const alternar = (): void => {
    setAbierta(v => {
      try { localStorage.setItem(`evo-riel:${clave}`, v ? '0' : '1'); } catch { /* sin almacenamiento */ }
      return !v;
    });
  };
  return (
    <div style={{ borderTop: '1px solid var(--evo-stroke)' }}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={abierta}
        onClick={alternar}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternar(); } }}
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-2) 0', cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-body)', color: abierta ? 'var(--evo-cyan)' : 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>{titulo}</span>
        <span style={{ ...menor, whiteSpace: 'nowrap' }}>{meta}</span>
        <span aria-hidden style={{ ...menor, flexShrink: 0 }}>{abierta ? '▴' : '▾'}</span>
      </div>
      {abierta && <div style={{ paddingBottom: 'var(--evo-space-3)' }}>{children}</div>}
    </div>
  );
}

export function RielMantenimiento({ project }: { project: ProjectRow }) {
  const projectId = project.id;
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const { data, isLoading } = useMantenimiento(projectId);
  const encender = useEncenderMantenimiento(projectId);
  const fijar = useFijarAutonomia(projectId);
  const ajustar = useAjustarRevision(projectId);
  const encendido = data?.enabled === true;
  const { data: sc } = useSearchConsole(projectId, encendido);

  if (isLoading) return <DetailSection title={t('projects.maintenance.title')}><LoadingState size="sm" /></DetailSection>;
  if (!data) return null;

  const fecha = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  const fallo = (e: unknown): void => { toast.error(e instanceof Error ? e.message : t('common.error')); };

  if (!encendido) {
    return (
      <DetailSection title={t('projects.maintenance.title')}>
        <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', lineHeight: 1.5 }}>
          {t('projects.maintenance.off.body')}
        </p>
        <div style={{ marginTop: 'var(--evo-space-3)' }}>
          <Button
            size="sm"
            tone="cyan"
            loading={encender.isPending}
            onClick={() => {
              // Encender congela la línea de base, y eso se dice en el botón y
              // no después: sin punto cero no hay contra qué comparar.
              encender.mutate(true, {
                onError: e => { toast.error(e instanceof Error ? e.message : t('projects.maintenance.off.needsSite')); },
              });
            }}
          >
            {t('projects.maintenance.off.cta')}
          </Button>
        </div>
      </DetailSection>
    );
  }

  async function cambiarAutonomia(nivel: Autonomia): Promise<void> {
    if (nivel === 'auto') {
      const ok = await confirm({
        title: t('projects.maintenance.autonomy.autoTitle'),
        description: t('projects.maintenance.autonomy.autoBody'),
        confirmLabel: t('projects.maintenance.autonomy.auto'),
        tone: 'warning',
      });
      if (!ok) return;
    }
    fijar.mutate(nivel, { onError: fallo });
  }

  async function apagar(): Promise<void> {
    const ok = await confirm({
      title: t('projects.maintenance.on.turnOffTitle'),
      description: t('projects.maintenance.on.turnOffBody'),
      confirmLabel: t('projects.maintenance.on.turnOff'),
      tone: 'warning',
    });
    if (!ok) return;
    encender.mutate(false, { onError: fallo });
  }

  // El punto cero es la primera medición. Sin ella, ninguna fecha inventada.
  const puntoCero = data.serie[0];
  const desde = [
    puntoCero ? t('projects.maintenance.rail.since', { fecha: fecha(puntoCero.auditedAt) }) : null,
    data.articulosPorSemana === 0
      ? t('projects.maintenance.articles.perWeekOff').toLowerCase()
      : t('projects.maintenance.articles.perWeek', { count: data.articulosPorSemana }),
    t(`projects.maintenance.autonomy.${data.autonomia}`).toLowerCase(),
  ].filter(Boolean).join(' · ');
  const c = data.comparacion;
  const resumen = c && c.desde.score != null && c.hasta.score != null
    ? t('projects.maintenance.rail.scoreChange', { antes: c.desde.score, despues: c.hasta.score, count: c.publicaciones })
    : t('projects.maintenance.rail.noComparison');

  // El dato de cada fila, corto: lo que se ve sin abrirla.
  const dato: Record<Fila, string> = {
    'search-console': sc?.conexion?.status === 'ok' ? t('projects.maintenance.gsc.connected') : t('projects.maintenance.gsc.notConnected'),
  };

  return (
    <>
      <DetailSection
        title={t('projects.maintenance.title')}
        titleRight={<Pill tone="cyan" variant="outline" size="xs">{t('projects.maintenance.rail.on')}</Pill>}
      >
        <div style={menor}>{desde}</div>
        <div style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', marginTop: '2px' }}>{resumen}</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', marginTop: 'var(--evo-space-3)', flexWrap: 'wrap' }}>
          <Dropdown
            size="xs"
            options={(['manual', 'semi', 'auto'] as Autonomia[]).map(n => ({ value: n, label: t(`projects.maintenance.autonomy.${n}`) }))}
            value={data.autonomia}
            onChange={n => { void cambiarAutonomia(n); }}
            disabled={fijar.isPending}
          />
          <Button size="xs" variant="link" tone="muted" loading={encender.isPending} onClick={() => { void apagar(); }}>
            {t('projects.maintenance.on.turnOff')}
          </Button>
        </div>

        {/* Cuándo corre la revisión semanal: día y hora, en la zona del negocio.
            Es lo que decide todo lo demás del ciclo, por eso está acá y no en
            un ajuste escondido. */}
        <div style={{ height: '1px', background: 'var(--evo-stroke)', margin: 'var(--evo-space-3) 0' }} />
        <div style={{ ...etiqueta, marginBottom: 'var(--evo-space-1)' }}>{t('projects.maintenance.rail.reviewDay')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
          <Dropdown
            size="xs"
            options={nombresDeDias(i18n.language)}
            value={String(data.revision.dia)}
            onChange={v => { ajustar.mutate({ dia: Number(v) }, { onError: fallo }); }}
            disabled={ajustar.isPending}
          />
          <Dropdown
            size="xs"
            options={Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: `${String(h).padStart(2, '0')}:00` }))}
            value={String(data.revision.hora)}
            onChange={v => { ajustar.mutate({ hora: Number(v) }, { onError: fallo }); }}
            disabled={ajustar.isPending}
          />
        </div>
        <div style={{ ...menor, marginTop: 'var(--evo-space-1)' }}>{t('projects.maintenance.rail.reviewZone')}</div>

        <div style={{ marginTop: 'var(--evo-space-3)' }}>
          <Acordeon clave={`${String(projectId)}:search-console`} titulo={t('projects.maintenance.rail.sections.search-console')} meta={dato['search-console']}>
            <CuerpoSearchConsole projectId={projectId} />
          </Acordeon>
        </div>
      </DetailSection>
      {dialog}
    </>
  );
}
